import React, { useEffect } from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Caption1,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading4,
  JustifyContent,
  type RadiusScale,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import { useDispatch, useSelector } from 'react-redux'

import { trackUserFreeFoodLinkShare } from 'actions/loggingmanager'
import { trackingReferFriend } from 'actions/user'
import type { ShareSurface } from 'components/SocialLinks/shareNatively'
import { SOCIAL_TYPES } from 'components/SocialLinks/socialReferralHelper'
import { isShareableOffer } from 'selectors/rafOffer'
import { getLoadingStateForOffer, getReferralCode, getReferralOffer } from 'selectors/user'

import { DesktopCopyShareLink } from './DesktopCopyShareLink'
import { MobileShareLink } from './MobileShareLink'
import { RafStepperProgress } from './RafStepperProgress'
import { trackAction } from './tracking/rafBannerTracking'
import type { ValidatedRafBannerData } from './useRafBanner'

type RafBannerCardProps = {
  data: ValidatedRafBannerData
  orderId: string
  surface: ShareSurface
}

// breakpoints[1] (768px), not [0] — the latter would merge tablet into desktop.
const Card = withCitrusTheme(Box, (theme) => ({
  borderTop: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  borderBottom: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    borderTop: 'none',
    borderBottom: 'none',
  },
}))

// Guards against double-firing "banner displayed" — mounted once per mobile/desktop placement
// for the same order. Reference-counted rather than a plain Set so the entry clears once every
// mounted instance for an order goes away, letting a later revisit (e.g. client-side navigation
// back to the same order without a full page reload) fire the event again.
const trackedOrderIds = new Set<string>()
const mountCountsByOrderId = new Map<string, number>()

// Purely presentational — callers own the useRafBanner call and the fallback decision. Tracks its
// own "displayed" event on mount, not from the hook, since the hook can resolve a banner the
// caller never actually renders (e.g. Market.presentation's showOrderConfirmationReceipt gate).
export const RafBannerCard = ({ data, orderId, surface }: RafBannerCardProps) => {
  const dispatch = useDispatch()
  const referralCode = useSelector(getReferralCode)
  const rafOffer = useSelector(getReferralOffer) || Immutable.Map()
  const isReferralOfferPending = useSelector(getLoadingStateForOffer)
  const { title, description, creditFormatted, footerText, action, steps } = data

  // referralCode/rafOffer load via a separate fetch than the one gating this card, so the CTAs
  // can otherwise mount before that data exists and share a link/message with no real values.
  const isReferralDataReady = Boolean(referralCode) && isShareableOffer(rafOffer)
  // Once that fetch has settled without producing shareable data, match the legacy
  // ReferAFriend's null-render rather than leaving a permanently disabled, dead CTA.
  const shouldRenderCard = isReferralDataReady || isReferralOfferPending

  useEffect(() => {
    if (!shouldRenderCard) {
      return undefined
    }

    mountCountsByOrderId.set(orderId, (mountCountsByOrderId.get(orderId) ?? 0) + 1)

    if (!trackedOrderIds.has(orderId)) {
      trackedOrderIds.add(orderId)
      trackAction(data.analytics)
    }

    return () => {
      const remaining = (mountCountsByOrderId.get(orderId) ?? 1) - 1

      if (remaining <= 0) {
        mountCountsByOrderId.delete(orderId)
        trackedOrderIds.delete(orderId)
      } else {
        mountCountsByOrderId.set(orderId, remaining)
      }
    }
    // Fire once per order per page visit, not on every re-render or every mounted instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, shouldRenderCard])

  if (!shouldRenderCard) {
    return null
  }

  return (
    <Card
      data-testid="raf-banner"
      bg={Color.White}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      paddingH={[4, 4, 0]}
      paddingV={[4, 4, 0]}
    >
      <Box display={Display.Flex} alignItems={AlignItems.FlexStart} gap={3}>
        <Box
          width="64px"
          height="64px"
          flexShrink={0}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          gap={0.5}
          paddingH={1}
          paddingV={2}
          borderRadius={4 as RadiusScale}
          bg={Color.Primary_600}
        >
          <Body2
            size={7}
            fontWeight={FontWeight.Bold}
            color={Color.White}
            textAlign={TextAlign.Center}
          >
            {creditFormatted}
          </Body2>
          <Caption1
            size={0}
            fontWeight={FontWeight.Bold}
            color={Color.White}
            textAlign={TextAlign.Center}
          >
            waiting
          </Caption1>
        </Box>

        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={1}
          style={{ flex: 1 }}
        >
          <Heading4 size={[5, 5, 7]} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
            {title}
          </Heading4>
          <Body2 size={[2, 2, 4]} color={Color.ColdGrey_800}>
            {description}
          </Body2>
        </Box>
      </Box>

      <RafStepperProgress steps={steps} />

      {/* Desktop copies the link and shows an inline success state instead of handing off
          into the native-share flow mobile/tablet use. */}
      <Box display={[Display.Block, Display.Block, Display.None, Display.None]}>
        <MobileShareLink
          referralCode={referralCode}
          rafOffer={rafOffer}
          surface={surface}
          label={action.title}
          successMessage={action.successMessage}
          isReady={isReferralDataReady}
          onShare={() => trackAction(action.analytics)}
          trackingReferFriend={(actionType: string, trackingType?: string) =>
            dispatch(trackingReferFriend(actionType, trackingType))
          }
          trackUserFreeFoodLinkShare={(payload: { target: string }) =>
            dispatch(trackUserFreeFoodLinkShare(payload))
          }
        />
      </Box>
      <Box display={[Display.None, Display.None, Display.Block, Display.Block]}>
        <DesktopCopyShareLink
          referralCode={referralCode}
          label={action.webTitle ?? action.title}
          successMessage={action.successMessage}
          isReady={isReferralDataReady}
          onClick={() => trackAction(action.analytics)}
          onCopy={() => dispatch(trackUserFreeFoodLinkShare({ target: SOCIAL_TYPES.link }))}
        />
      </Box>

      <Caption1 size={0} textAlign={TextAlign.Center} color={Color.ColdGrey_600}>
        {footerText}
      </Caption1>
    </Card>
  )
}
